import { useState, useEffect } from 'react';
import { apiRawFetch } from '../../main/client';

interface TaxComponentResult {
    id: string;
    name: string;
    category: 'indirect' | 'income' | 'social' | 'other';
    amount: number;
    rate: number;
}

interface TaxPresetSummary {
    id: string;
    name: string;
    country: string;
    entityType: 'individual' | 'company';
    description: string;
}

export interface YearlyTaxBreakdown {
    preset: TaxPresetSummary;
    grossRevenue: number;
    breakdown: TaxComponentResult[];
    totalsByCategory: Record<string, number>;
    totalTaxes: number;
    netIncome: number;
    effectiveRate: number;
    currency: string;
    bookingsCount: number;
    daysHosted: number;
    year: number;
}

interface StatisticsResponse {
    years: YearlyTaxBreakdown[];
    hasPreset: boolean;
    presetId: string | null;
    currency: string;
}

const useYearlyStatistics = (propertyId: number | undefined) => {
    const [data, setData] = useState<StatisticsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!propertyId) {
            setLoading(false);
            return;
        }

        const fetchStatistics = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await apiRawFetch(
                    `api/properties/${propertyId}/financial/statistics`
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch statistics');
                }

                const { data: responseData } = await response.json();
                setData(responseData);
            } catch (err) {
                setError(err instanceof Error ? err : new Error('Unknown error'));
            } finally {
                setLoading(false);
            }
        };

        fetchStatistics();
    }, [propertyId]);

    return { data, loading, error };
};

export default useYearlyStatistics;
